import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { ArrowLeft, ArrowRight, Loader2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { errMsg, useApi } from "@/lib/api";
import ModeStep from "./ModeStep";
import ClientStep from "./ClientStep";
import PreviewStep from "./PreviewStep";

const STEPS = ["Modalità", "Nuovo cliente", "Anteprima e costi"];
const DEFAULT_POLICY = { simple: "keep", same_sector: "stock", new_sector: "stock" };
const emptyClient = (src) => ({ project_name: `${src?.name || "Progetto"} (copia)`, company_name: "", sector: src?.sector || "", description: "", services: "",
  audience: "", goal: "", language: "", email: "", phone: "", address: "", website: "", primary_color: "", fonts: "", notes: "" });

export default function DuplicateDialog({ source, open, onOpenChange }) {
  const api = useApi();
  const navigate = useNavigate();
  const [step, setStep] = useState(0);
  const [mode, setModeState] = useState("simple");
  const [client, setClient] = useState(emptyClient(source));
  const [images, setImages] = useState({ policy: "keep", keep_client_images: false, model: "nano-banana-pro" });
  const [models, setModels] = useState([]);
  const [preview, setPreview] = useState(null);
  const [rows, setRows] = useState([]);
  const [custom, setCustom] = useState([]);
  const [confirmed, setConfirmed] = useState(false);
  const [busy, setBusy] = useState("");

  useEffect(() => {
    if (!open) return;
    setStep(0); setModeState("simple"); setClient(emptyClient(source)); setPreview(null); setRows([]); setCustom([]); setConfirmed(false); setBusy("");
    setImages({ policy: "keep", keep_client_images: false, model: "nano-banana-pro" });
    api.get("/media/models").then(({ data }) => setModels(data)).catch(() => {});
  }, [open, source?.id]); // eslint-disable-line react-hooks/exhaustive-deps

  const setMode = (m) => { setModeState(m); setImages((x) => ({ ...x, policy: DEFAULT_POLICY[m] })); };
  const valid = [true, client.project_name.trim() && client.company_name.trim() && (mode !== "new_sector" || (client.sector.trim() && (client.description.trim() || client.services.trim()))), true];

  const loadPreview = async () => {
    setBusy("preview");
    try {
      const { data } = await api.post(`/projects/${source.id}/duplicate/preview`, { mode, client, images });
      setPreview(data); setRows(data.detected.map((d) => ({ ...d, new: d.suggestion, on: d.on !== false }))); setConfirmed(false); setStep(2);
    } catch (e) { toast.error(errMsg(e)); }
    setBusy("");
  };
  const run = async () => {
    setBusy("run");
    const replacements = mode === "new_sector" ? null : [...rows.filter((r) => r.on).map((r) => ({ old: r.value, new: r.new })),
      ...custom.filter((c) => c.old.trim().length >= 2).map((c) => ({ old: c.old, new: c.new }))];
    try {
      const { data } = await api.post(`/projects/${source.id}/duplicate/run`, { mode, client, images, replacements, confirm_usd: preview.estimate.total_usd });
      data.warnings?.forEach((w) => toast.warning(w));
      toast.success(data.job_id ? "Copia creata: l'AI la sta adattando" : `Creato «${data.project.name}»`);
      onOpenChange(false);
      navigate(`/projects/${data.project.id}`);
    } catch (e) { toast.error(errMsg(e)); setBusy(""); }
  };
  const needsConfirm = preview && preview.estimate.total_usd > 0;
  const canRun = preview && !preview.estimate.over_limit && !preview.estimate.blocked && (!needsConfirm || confirmed);

  return (
    <Dialog open={open} onOpenChange={(o) => !busy && onOpenChange(o)}>
      <DialogContent className="sm:max-w-3xl max-h-[92vh] overflow-y-auto" data-testid="duplicate-dialog">
        <DialogHeader>
          <DialogTitle className="pr-8">Duplica «{source?.name}»</DialogTitle>
          <DialogDescription>{source?.is_template ? "Nuovo sito a partire da questo template." : "Crea un nuovo progetto indipendente per un altro cliente."}</DialogDescription>
        </DialogHeader>
        <ol className="flex flex-wrap gap-2" data-testid="dup-steps">
          {STEPS.map((s, i) => (
            <li key={s} className={`flex items-center gap-2 rounded-full px-3 py-1 text-xs ${i === step ? "bg-navy-900 text-white" : i < step ? "bg-sky-100 text-sky-800" : "bg-slate-100 text-slate-500"}`}>
              <span className="font-semibold tabular-nums">{i + 1}</span>{s}
            </li>
          ))}
        </ol>
        <div className="pt-1">
          {step === 0 && <ModeStep mode={mode} setMode={setMode} />}
          {step === 1 && <ClientStep mode={mode} client={client} setClient={setClient} images={images} setImages={setImages} models={models} />}
          {step === 2 && preview && <PreviewStep preview={preview} rows={rows} setRows={setRows} custom={custom} setCustom={setCustom} confirmed={confirmed} setConfirmed={setConfirmed} />}
        </div>
        <div className="flex items-center justify-between border-t pt-4">
          <Button variant="ghost" onClick={() => setStep(step - 1)} disabled={step === 0 || !!busy} data-testid="dup-prev-btn"><ArrowLeft size={15} className="mr-1.5" />Indietro</Button>
          {step === 0 && <Button onClick={() => setStep(1)} className="bg-navy-900 hover:bg-navy-800" data-testid="dup-next-btn">Avanti<ArrowRight size={15} className="ml-1.5" /></Button>}
          {step === 1 && (
            <Button onClick={loadPreview} disabled={!valid[1] || !!busy} className="bg-navy-900 hover:bg-navy-800" data-testid="dup-preview-btn">
              {busy === "preview" ? <Loader2 size={15} className="mr-1.5 animate-spin" /> : null}Anteprima e costi<ArrowRight size={15} className="ml-1.5" />
            </Button>
          )}
          {step === 2 && (
            <Button onClick={run} disabled={!canRun || !!busy} className="bg-sky-600 hover:bg-sky-700" data-testid="dup-run-btn">
              {busy === "run" && <Loader2 size={15} className="mr-1.5 animate-spin" />}{mode === "simple" ? "Crea la copia" : "Conferma e avvia"}
            </Button>
          )}
        </div>
        {step === 1 && !valid[1] && <p className="text-xs text-red-600" data-testid="dup-validation-msg">{mode === "new_sector" ? "Indica nome progetto, nome cliente, nuovo settore e una descrizione o i servizi." : "Indica nome del progetto e nome del nuovo cliente."}</p>}
      </DialogContent>
    </Dialog>
  );
}
